import React, { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ArrowLeft } from 'lucide-react';
import { publications, categoryConfig } from '../../data/publications';

const SearchResultSkeleton = () => (
  <div className="bg-white rounded-lg shadow-lg overflow-hidden animate-pulse">
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-6 w-32 bg-slate-200 rounded-full" />
        <div className="h-4 w-24 bg-slate-200 rounded" />
      </div>
      <div className="h-6 bg-slate-200 rounded mb-3 w-3/4" />
      <div className="space-y-2 mb-4">
        <div className="h-4 bg-slate-200 rounded w-full" />
        <div className="h-4 bg-slate-200 rounded w-5/6" />
        <div className="h-4 bg-slate-200 rounded w-4/5" />
      </div>
      <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
        <div className="h-6 w-20 bg-slate-200 rounded-full" />
        <div className="h-6 w-24 bg-slate-200 rounded-full" />
        <div className="h-6 w-28 bg-slate-200 rounded-full" />
      </div>
    </div>
  </div>
);

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('search') || '';
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (query.trim()) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [query]);

  const results = useMemo(() => {
    if (!query.trim()) return [];

    const searchTerm = query.toLowerCase();

    return publications.filter((pub) => {
      const categoryTitle = categoryConfig[pub.category as keyof typeof categoryConfig]?.title || '';
      const searchableText = `
        ${pub.title}
        ${pub.abstract}
        ${pub.keywords.join(' ')}
        ${categoryTitle}
      `.toLowerCase();

      return searchableText.includes(searchTerm);
    });
  }, [query]);

  const highlightText = (text: string) => {
    if (!query.trim()) return text;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));

    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ?
        <mark key={index} className="bg-[#D4AF37]/30 text-[#064635] px-1 rounded">{part}</mark> :
        part
    );
  };

  const truncateAbstract = (text: string, maxLength: number = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  if (!query.trim()) {
    return (
      <div className="min-h-screen bg-[#F5F5DC] pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center mb-8">
            <Link to="/" className="mr-4">
              <ArrowLeft className="h-6 w-6 text-[#064635] hover:text-[#D4AF37] transition" />
            </Link>
            <h1 className="text-3xl font-bold text-[#064635]">Search Publications</h1>
          </div>
          <div className="text-center py-12">
            <Search className="h-12 w-12 text-[#064635] mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#064635] mb-2">Start Searching</h2>
            <p className="text-gray-600 mb-6">
              Enter a search term to explore our collection of 72 publications
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-[#064635] text-white rounded-lg
                       hover:bg-opacity-90 transition"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center mb-8">
          <Link to="/" className="mr-4">
            <ArrowLeft className="h-6 w-6 text-[#064635] hover:text-[#D4AF37] transition" />
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-[#064635]">
              Search Results for "{query}"
            </h1>
            <p className="text-gray-600 mt-2">
              Found {results.length} {results.length === 1 ? 'publication' : 'publications'}
            </p>
          </div>
        </div>

        {results.length === 0 ? (
          <div className="text-center py-12">
            <Search className="h-16 w-16 text-[#064635] mx-auto mb-4 opacity-50" />
            <h2 className="text-2xl font-semibold text-[#064635] mb-4">No results found</h2>
            <p className="text-gray-600 mb-4">
              We couldn't find any publications matching "{query}"
            </p>
            <div className="max-w-md mx-auto text-left bg-white rounded-lg shadow-lg p-6 mb-6">
              <h3 className="font-semibold text-[#064635] mb-3">Try:</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• Using different keywords</li>
                <li>• Checking your spelling</li>
                <li>• Using more general terms</li>
                <li>• Browsing our collections by category</li>
              </ul>
            </div>
            <div className="flex gap-4 justify-center">
              <Link
                to="/"
                className="inline-block px-6 py-3 bg-[#064635] text-white rounded-lg
                         hover:bg-opacity-90 transition"
              >
                Browse All Publications
              </Link>
              <Link
                to="/about/author"
                className="inline-block px-6 py-3 border-2 border-[#D4AF37] text-[#064635] rounded-lg
                         hover:bg-[#D4AF37]/10 transition"
              >
                View by Category
              </Link>
            </div>
          </div>
        ) : isLoading ? (
          <div className="space-y-6">
            {Array.from({ length: 5 }).map((_, index) => (
              <SearchResultSkeleton key={index} />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {results.map((result) => {
              const category = categoryConfig[result.category as keyof typeof categoryConfig];
              return (
                <Link
                  key={result.id}
                  to={`/publications/${result.id}`}
                  className="block bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl
                           transition-all duration-200 transform hover:-translate-y-1"
                >
                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                                     bg-[#D4AF37]/20 text-[#064635] border border-[#D4AF37]/30">
                        {category?.title || result.category}
                      </span>
                      <span className="text-sm text-gray-500">
                        {result.date}
                      </span>
                    </div>

                    <h2 className="text-xl font-semibold text-[#064635] mb-3 hover:text-[#D4AF37] transition">
                      {highlightText(result.title)}
                    </h2>

                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {highlightText(truncateAbstract(result.abstract))}
                    </p>

                    {result.keywords && result.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                        {result.keywords.slice(0, 6).map((keyword, index) => (
                          <span
                            key={index}
                            className="px-2.5 py-1 bg-[#F5F5DC] text-[#064635] text-xs rounded-full
                                     border border-gray-200"
                          >
                            {highlightText(keyword)}
                          </span>
                        ))}
                        {result.keywords.length > 6 && (
                          <span className="px-2.5 py-1 text-gray-500 text-xs">
                            +{result.keywords.length - 6} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}